import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tabs,
  Tab,
  ImageList,
  ImageListItem,
} from '@mui/material';
import {
  Phone,
  WhatsApp,
  Close,
  Star,
  CheckCircle,
  Speed,
  Security,
  Support,
  LocationOn,
  ArrowBackIos,
  ArrowForwardIos,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import ServiceCard from '../components/ServiceCard';
import ServiceForm from '../components/ServiceForm';
import Map from '../components/Map';
import { useTheme } from '../context/ThemeContext';

const Home: React.FC = () => {
  const { theme: customTheme } = useTheme();
  const [selectedService, setSelectedService] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [showWhatsAppOptions, setShowWhatsAppOptions] = useState(false);
  const [galleryTab, setGalleryTab] = useState(0);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const services = [
    {
      id: 'civil-work',
      title: 'Civil Work',
      description: 'Complete civil construction from foundation to finishing touches',
      image: 'https://images.unsplash.com/photo-1574313428745-ea9221d581ee?q=80&w=1471&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
      imageAlt: 'Civil construction and building contractor work in Bangalore by Ishaan Construction Works',
      features: ['Foundation Work', 'Structural Construction', 'Quality Materials', 'Timely Delivery'],
    },
    {
      id: 'plumbing',
      title: 'Plumbing',
      description: 'Complete plumbing solutions for residential and commercial properties',
      image: '/plum.jpeg',
      imageAlt: 'Plumbing services near HSR Layout and Bangalore — leaks, bathrooms, kitchens and drainage',
      features: ['Water Supply', 'Drainage Systems', 'Fixture Installation', 'Maintenance'],
    },
    {
      id: 'carpenter',
      title: 'Carpenter',
      description: 'Professional carpentry services for furniture and woodwork',
      image: 'https://images.unsplash.com/photo-1687422810663-c316494f725a?q=80&w=1470&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
      imageAlt: 'Carpenter and woodwork services near Koramangala and Bangalore — wardrobes, doors and repairs',
      features: ['Custom Furniture', 'Wooden Structures', 'Repairs', 'Installation'],
    },
    {
      id: 'false-ceiling',
      title: 'False Ceiling',
      description: 'Modern false ceiling solutions for enhanced aesthetics',
      image: 'https://plus.unsplash.com/premium_photo-1682361002186-c5593b88ebfe?q=80&w=687&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
      imageAlt: 'False ceiling contractors in Bangalore — gypsum and POP ceilings for homes and offices',
      features: ['Gypsum Ceiling', 'POP Ceiling', 'LED Integration', 'Design Options'],
    },
    {
      id: 'networking',
      title: 'Networking',
      description: 'Complete networking solutions for homes and offices',
      image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=500&h=300&fit=crop',
      imageAlt: 'Home and office networking setup in Bangalore — Wi-Fi, LAN and structured cabling',
      features: ['WiFi Setup', 'Cable Management', 'Network Security', 'Maintenance'],
    },
    {
      id: 'cctv',
      title: 'CCTV',
      description: 'Security camera installation and monitoring solutions',
      image: '/cctv.jpeg',
      imageAlt: 'CCTV installation near me Bangalore — HD cameras and remote viewing by Ishaan Construction Works',
      features: ['HD Cameras', 'Remote Monitoring', 'Night Vision', 'Mobile App'],
    },
    {
      id: 'electrical',
      title: 'All Electrical Work',
      description: 'Complete electrical solutions including wiring, fixtures, and maintenance',
      image: '/electric.jpeg',
      imageAlt: 'Electrician and electrical work in HSR Layout Bangalore — wiring, DB and fixtures',
      features: ['Wiring', 'Fixture Installation', 'Panel Work', 'Safety Compliance'],
    },
    {
      id: 'interior-design',
      title: 'Interior Design',
      description: 'Creative interior design solutions for modern living spaces',
      image: '/interior.jpeg',
      imageAlt: 'Interior design near HSR Layout Bangalore — space planning, finishes and turnkey styling',
      features: ['Space Planning', 'Color Schemes', 'Furniture Selection', 'Lighting Design'],
    },
    {
      id: 'steel-fabrication',
      title: 'Steel Fabrication',
      description: 'Custom steel fabrication for structural and decorative purposes',
      image: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=500&h=300&fit=crop',
      imageAlt: 'Steel fabrication Bangalore — gates, railings and custom metal work',
      features: ['Custom Structures', 'Welding Services', 'Metal Work', 'Quality Assurance'],
    },
  ];

  const features = [
    {
      icon: <CheckCircle />,
      title: 'Quality Assurance',
      description: 'We use only the finest materials and follow strict quality standards',
    },
    {
      icon: <Speed />,
      title: 'Timely Delivery',
      description: 'We complete projects on time without compromising on quality',
    },
    {
      icon: <Support />,
      title: '24/7 Support',
      description: 'Round-the-clock customer support for all your needs',
    },
  ];

  const ongoingProjects = [
    {
      id: 1,
      title: 'Building Renovation',
      location: 'HSR Layout, Bangalore',
      progress: 75,
      image: 'https://images.unsplash.com/photo-1557479961-7d98533b22f3?q=80&w=1074&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
      description: 'Complete building renovation with modern amenities and structural improvements',
    },
    {
      id: 2,
      title: 'Interior Works',
      location: 'Koramangala, Bangalore',
      progress: 60,
      image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=500&h=300&fit=crop',
      description: 'Interior design and finishing works for residential space',
    },
    {
      id: 3,
      title: 'Small Scale Construction',
      location: 'Whitefield, Bangalore',
      progress: 40,
      image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=500&h=300&fit=crop',
      description: 'Small-scale construction project with quality workmanship',
    },
  ];

  const galleryImages = {
    interior: [
      '/gallery/interior/1.jpeg',
      '/gallery/interior/2.jpeg',
      '/gallery/interior/3.jpeg',
      '/gallery/interior/4.jpeg',
      '/gallery/interior/5.jpeg',
      '/gallery/interior/6.jpeg',
      '/gallery/interior/7.jpeg',
      '/gallery/interior/int1.jpeg',
      '/gallery/interior/int2.jpeg',
      '/gallery/interior/int3.jpeg',
      '/gallery/interior/int4.jpeg',
      '/gallery/interior/int5.jpeg',
      '/gallery/interior/int6.jpeg',
      '/gallery/interior/int7.jpeg',
      '/gallery/interior/int8.jpeg',
      '/gallery/interior/int9.jpeg',
      '/gallery/interior/int10.jpeg',
      '/gallery/interior/int11.jpeg',
      '/gallery/interior/int12.jpeg',
      '/gallery/interior/int13.jpeg',
      '/gallery/interior/int14.jpeg',
      '/gallery/interior/int15.jpeg',
      '/gallery/interior/int16.jpeg',
      '/gallery/interior/int17.jpeg',
      '/gallery/interior/int18.jpeg',
      '/gallery/interior/int19.jpeg',
      '/gallery/interior/int20.jpeg',
      '/gallery/interior/int21.jpeg',
      '/gallery/interior/int22.jpeg',
      '/gallery/interior/int23.jpeg',
      '/gallery/interior/int24.jpeg',
      '/gallery/interior/int25.jpeg',
    ],
    exterior: [
      '/gallery/exterior/exte1.jpeg',
      '/gallery/exterior/exr2.jpeg',
      '/gallery/exterior/exte3.jpeg',
      '/gallery/exterior/exte4.jpeg',
      '/gallery/exterior/exte5.jpeg',
      '/gallery/exterior/exte6.jpeg',
    ],
    construction: [
      '/gallery/construction/con1.jpeg',
      '/gallery/construction/con2.jpeg',
      '/gallery/construction/con3.jpeg',
      '/gallery/construction/con4.jpeg',
      '/gallery/construction/con6.jpeg',
    ],
    plumbing: [
      '/gallery/plumbing/plum1.jpeg',
      '/gallery/plumbing/plum2.jpeg',
      '/gallery/plumbing/plum3.jpeg',
      '/gallery/plumbing/plum4.jpeg',
      '/gallery/plumbing/plum5.jpeg',
      '/gallery/plumbing/plum6.jpeg',
      '/gallery/plumbing/plum7.jpeg',
      '/gallery/plumbing/plum8.jpeg',
      '/gallery/plumbing/plum9.jpeg',
      '/gallery/plumbing/plum10.jpeg',
      '/gallery/plumbing/plum11.jpeg',
    ],
    cctv: [
      '/gallery/cameras/WhatsApp Image 2025-09-30 at 19.01.36.jpeg',
      '/gallery/cameras/WhatsApp Image 2025-09-30 at 19.12.44.jpeg',
      '/gallery/cameras/WhatsApp Image 2025-09-30 at 19.12.48.jpeg',
      '/gallery/cameras/WhatsApp Image 2025-09-30 at 19.12.52.jpeg',
      '/gallery/cameras/WhatsApp Image 2025-09-30 at 19.12.54.jpeg',
      '/gallery/cameras/WhatsApp Image 2025-09-30 at 19.23.03.jpeg',
      '/gallery/cameras/WhatsApp Image 2025-09-30 at 19.23.04.jpeg',
      '/gallery/cameras/WhatsApp Image 2025-09-30 at 19.23.05.jpeg',
      '/gallery/cameras/WhatsApp Image 2025-09-30 at 19.23.06 (1).jpeg',
      '/gallery/cameras/WhatsApp Image 2025-09-30 at 19.23.08.jpeg',
      '/gallery/cameras/WhatsApp Image 2025-09-30 at 19.23.13.jpeg',
      '/gallery/cameras/WhatsApp Image 2025-09-30 at 19.23.16.jpeg',
      '/gallery/cameras/WhatsApp Image 2025-09-30 at 19.23.17.jpeg',
      '/gallery/cameras/WhatsApp Image 2025-09-30 at 19.23.18.jpeg',
    ],
    electrical: [
      '/gallery/electrical/WhatsApp Image 2025-09-30 at 19.01.36.jpeg',
      '/gallery/electrical/WhatsApp Image 2025-09-30 at 19.12.56.jpeg',
    ],
  };

  const handleServiceSelect = (serviceId: string) => {
    setSelectedService(serviceId);
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setSelectedService(null);
  };

  const handleCallNow = (phoneNumber: string) => {
    window.open(`tel:${phoneNumber}`);
  };

  const handleWhatsApp = (phoneNumber: string) => {
    const message = 'Hi, I am interested in your construction services. Please provide more information.';
    window.open(`https://wa.me/91${phoneNumber}?text=${encodeURIComponent(message)}`);
  };

  const handleServiceRequest = (serviceId: string) => {
    const service = services.find(s => s.id === serviceId);
    const message = `New Service Request:\n\nService: ${service?.title}\nDescription: ${service?.description}\n\nPlease contact the customer for more details.`;
    
    // Send to both numbers
    const phoneNumbers = ['8074635096', '9972555661'];
    phoneNumbers.forEach((phone, index) => {
      setTimeout(() => {
        window.open(`https://wa.me/91${phone}?text=${encodeURIComponent(message)}`);
      }, index * 1000); // Delay between messages
    });
  };

  const handleWhatsAppOptions = () => {
    setShowWhatsAppOptions(true);
  };

  const handleCloseWhatsAppOptions = () => {
    setShowWhatsAppOptions(false);
  };

  const handleImageClick = (image: string, index: number) => {
    setSelectedImage(image);
    setCurrentImageIndex(index);
  };

  const handleCloseImage = () => {
    setSelectedImage(null);
  };

  const handleNextImage = () => {
    const currentGallery = getCurrentGallery();
    const nextIndex = (currentImageIndex + 1) % currentGallery.length;
    setCurrentImageIndex(nextIndex);
    setSelectedImage(currentGallery[nextIndex]);
  };

  const handlePrevImage = () => {
    const currentGallery = getCurrentGallery();
    const prevIndex = currentImageIndex === 0 ? currentGallery.length - 1 : currentImageIndex - 1;
    setCurrentImageIndex(prevIndex);
    setSelectedImage(currentGallery[prevIndex]);
  };

  const getCurrentGallery = () => {
    const galleries = [
      galleryImages.cctv,
      galleryImages.interior,
      galleryImages.exterior,
      galleryImages.construction,
      galleryImages.plumbing,
      galleryImages.electrical,
    ];
    return galleries[galleryTab] || [];
  };

  // Cleanup effect to ensure body scroll is restored
  useEffect(() => {
    return () => {
      // Cleanup on unmount
      document.body.style.overflow = '';
    };
  }, []);

  // Additional cleanup when dialogs close
  useEffect(() => {
    if (!showForm && !showWhatsAppOptions) {
      // Ensure scroll is restored when all dialogs are closed
      setTimeout(() => {
        document.body.style.overflow = '';
      }, 100);
    }
  }, [showForm, showWhatsAppOptions]);

  return (
    <Box>
      {/* Hero Section */}
      <Box
        id="home"
        sx={{
          background: 'linear-gradient(135deg, #5e72e4 0%, #825ee4 20%, #d946ef 40%, #ff6b9d 60%, #ffa447 80%, #ffcc70 100%)',
          py: { xs: 8, md: 10 },
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(180deg, rgba(255,255,255,0.75) 0%, rgba(255,255,255,0.7) 100%)',
            zIndex: 0,
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <Typography variant="h2" component="h1" gutterBottom fontWeight="bold">
                  Building Dreams,
                  <br />
                  <Box component="span" color="primary.main">
                    Creating Excellence
                  </Box>
                </Typography>
                <Typography variant="h6" color="text.secondary" paragraph>
                  One trusted team in Bangalore for civil work, plumbing, electrical, CCTV,
                  carpentry, false ceiling, networking, interior design and steel fabrication.
                  Based in HSR Layout — serving Koramangala, BTM Layout, Whitefield and nearby
                  areas for over 10 years.
                </Typography>
                <Box display="flex" gap={2} flexWrap="wrap">
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<Phone />}
                    onClick={() => handleCallNow('8074635096')}
                    sx={{
                      background: `linear-gradient(135deg, ${customTheme.primaryColor} 0%, ${customTheme.accentColor} 100%)`,
                      minWidth: '200px',
                      height: '48px',
                      '&:hover': {
                        background: `linear-gradient(135deg, ${customTheme.primaryColor}dd 0%, ${customTheme.accentColor}dd 100%)`,
                      },
                    }}
                  >
                    Call: 8074635096
                  </Button>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<Phone />}
                    onClick={() => handleCallNow('9972555661')}
                    sx={{
                      background: `linear-gradient(135deg, ${customTheme.primaryColor} 0%, ${customTheme.accentColor} 100%)`,
                      minWidth: '200px',
                      height: '48px',
                      '&:hover': {
                        background: `linear-gradient(135deg, ${customTheme.primaryColor}dd 0%, ${customTheme.accentColor}dd 100%)`,
                      },
                    }}
                  >
                    Call: 9972555661
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    startIcon={<WhatsApp />}
                    onClick={() => handleWhatsApp('8074635096')}
                    sx={{
                      borderColor: customTheme.accentColor,
                      color: customTheme.accentColor,
                      minWidth: '200px',
                      height: '48px',
                      '&:hover': {
                        borderColor: customTheme.accentColor,
                        backgroundColor: customTheme.accentColor + '10',
                      },
                    }}
                  >
                    WhatsApp: 8074635096
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    startIcon={<WhatsApp />}
                    onClick={() => handleWhatsApp('9972555661')}
                    sx={{
                      borderColor: customTheme.accentColor,
                      color: customTheme.accentColor,
                      minWidth: '200px',
                      height: '48px',
                      '&:hover': {
                        borderColor: customTheme.accentColor,
                        backgroundColor: customTheme.accentColor + '10',
                      },
                    }}
                  >
                    WhatsApp: 9972555661
                  </Button>
                </Box>
              </motion.div>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: '-12px',
                      left: '-12px',
                      right: '12px',
                      bottom: '12px',
                      borderRadius: 4,
                      background: `linear-gradient(135deg, ${customTheme.primaryColor}30 0%, ${customTheme.accentColor}30 100%)`,
                      zIndex: 0,
                      filter: 'blur(20px)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      position: 'relative',
                      borderRadius: 4,
                      overflow: 'hidden',
                      boxShadow: '0 24px 48px rgba(0,0,0,0.2)',
                      border: '3px solid',
                      borderColor: 'background.paper',
                      zIndex: 1,
                      width: '100%',
                      height: { xs: 290, sm: 330, md: 420 },
                    }}
                  >
                    <Box
                      component="img"
                      src="/con.jpeg"
                      alt="Construction site — Ishaan Construction Works, Bangalore"
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        objectPosition: 'center center',
                        display: 'block',
                      }}
                    />
                    {/* Gradient Overlay */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: `linear-gradient(135deg, ${customTheme.primaryColor}15 0%, transparent 40%, ${customTheme.accentColor}10 100%)`,
                        pointerEvents: 'none',
                      }}
                    />
                    {/* Bottom Fade */}
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: '30%',
                        background: 'linear-gradient(to top, rgba(0,0,0,0.3) 0%, transparent 100%)',
                        pointerEvents: 'none',
                      }}
                    />
                  </Box>
                </Box>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box py={8}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" textAlign="center" gutterBottom fontWeight="bold">
            Why Choose Us?
          </Typography>
          <Typography variant="h6" color="text.secondary" textAlign="center" paragraph mb={6}>
            From civil and interiors to plumbing, electrical, CCTV and more — dependable quality
            on every trade we handle in Bangalore
          </Typography>
          
          <Grid container spacing={4} justifyContent="center">
            {features.map((feature, index) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Card
                    sx={{
                      height: '100%',
                      textAlign: 'center',
                      p: 3,
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <CardContent>
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: '50%',
                          background: `linear-gradient(135deg, ${customTheme.primaryColor} 0%, ${customTheme.accentColor} 100%)`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mx: 'auto',
                          mb: 3,
                        }}
                      >
                        {React.cloneElement(feature.icon, { sx: { fontSize: 40, color: 'white' } })}
                      </Box>
                      <Typography variant="h6" component="h3" gutterBottom fontWeight="bold">
                        {feature.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {feature.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Services Section */}
      <Box id="services" py={8} sx={{ backgroundColor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" textAlign="center" gutterBottom fontWeight="bold">
            Our Services
          </Typography>
          <Typography variant="h6" color="text.secondary" textAlign="center" paragraph mb={6}>
            Whether you searched for a plumber, electrician, CCTV installer or full construction
            support near HSR Layout, we cover every trade under one roof across Bangalore.
          </Typography>
          
          <Grid container spacing={4}>
            {services.map((service, index) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={service.id}>
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <ServiceCard
                    title={service.title}
                    description={service.description}
                    image={service.image}
                    imageAlt={service.imageAlt}
                    features={service.features}
                    onSelect={() => handleServiceSelect(service.id)}
                    isSelected={selectedService === service.id}
                  />
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Ongoing Projects Section */}
      <Box py={8} sx={{ backgroundColor: 'background.default' }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" textAlign="center" gutterBottom fontWeight="bold">
            Ongoing Projects
          </Typography>
          <Typography variant="h6" color="text.secondary" textAlign="center" paragraph mb={6}>
            Take a look at our current construction projects and their progress
          </Typography>
          
          <Grid container spacing={4}>
            {ongoingProjects.map((project, index) => (
              <Grid size={{ xs: 12, md: 4 }} key={project.id}>
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Card
                    sx={{
                      height: '100%',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <Box
                      component="img"
                      src={project.image}
                      alt={project.title}
                      sx={{
                        width: '100%',
                        height: 200,
                        objectFit: 'cover',
                      }}
                    />
                    <CardContent>
                      <Typography variant="h6" component="h3" gutterBottom fontWeight="bold">
                        {project.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        {project.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Gallery Section */}
      <Box id="gallery" py={8} sx={{ backgroundColor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" textAlign="center" gutterBottom fontWeight="bold">
            Our Work Gallery
          </Typography>
          <Typography variant="h6" color="text.secondary" textAlign="center" paragraph mb={6}>
            Explore our completed projects and see the quality of our work
          </Typography>
          
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
            <Tabs
              value={galleryTab}
              onChange={(e, newValue) => setGalleryTab(newValue)}
              centered
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTab-root': {
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '1rem',
                  minWidth: 'auto',
                  px: 2,
                },
              }}
            >
              <Tab label="CCTV" />
              <Tab label="Interior" />
              <Tab label="Exterior" />
              <Tab label="Construction" />
              <Tab label="Plumbing" />
              <Tab label="Electrical" />
            </Tabs>
          </Box>

          <Box>
            {galleryTab === 0 && (
              <ImageList variant="masonry" cols={3} gap={8}>
                {galleryImages.cctv.map((image, index) => (
                  <ImageListItem key={index}>
                    <img
                      src={image}
                      alt={`CCTV Installation ${index + 1}`}
                      loading="lazy"
                      onClick={() => handleImageClick(image, index)}
                      style={{
                        borderRadius: 8,
                        cursor: 'pointer',
                        transition: 'transform 0.3s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.05)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                    />
                  </ImageListItem>
                ))}
              </ImageList>
            )}
            
            {galleryTab === 1 && (
              <ImageList variant="masonry" cols={3} gap={8}>
                {galleryImages.interior.map((image, index) => (
                  <ImageListItem key={index}>
                    <img
                      src={image}
                      alt={`Interior Work ${index + 1}`}
                      loading="lazy"
                      onClick={() => handleImageClick(image, index)}
                      style={{
                        borderRadius: 8,
                        cursor: 'pointer',
                        transition: 'transform 0.3s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.05)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                    />
                  </ImageListItem>
                ))}
              </ImageList>
            )}
            
            {galleryTab === 2 && (
              <ImageList variant="masonry" cols={3} gap={8}>
                {galleryImages.exterior.map((image, index) => (
                  <ImageListItem key={index}>
                    <img
                      src={image}
                      alt={`Exterior Work ${index + 1}`}
                      loading="lazy"
                      onClick={() => handleImageClick(image, index)}
                      style={{
                        borderRadius: 8,
                        cursor: 'pointer',
                        transition: 'transform 0.3s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.05)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                    />
                  </ImageListItem>
                ))}
              </ImageList>
            )}
            
            {galleryTab === 3 && (
              <ImageList variant="masonry" cols={3} gap={8}>
                {galleryImages.construction.map((image, index) => (
                  <ImageListItem key={index}>
                    <img
                      src={image}
                      alt={`Construction Work ${index + 1}`}
                      loading="lazy"
                      onClick={() => handleImageClick(image, index)}
                      style={{
                        borderRadius: 8,
                        cursor: 'pointer',
                        transition: 'transform 0.3s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.05)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                    />
                  </ImageListItem>
                ))}
              </ImageList>
            )}
            
            {galleryTab === 4 && (
              <ImageList variant="masonry" cols={3} gap={8}>
                {galleryImages.plumbing.map((image, index) => (
                  <ImageListItem key={index}>
                    <img
                      src={image}
                      alt={`Plumbing Work ${index + 1}`}
                      loading="lazy"
                      onClick={() => handleImageClick(image, index)}
                      style={{
                        borderRadius: 8,
                        cursor: 'pointer',
                        transition: 'transform 0.3s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.05)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                    />
                  </ImageListItem>
                ))}
              </ImageList>
            )}
            
            {galleryTab === 5 && (
              <ImageList variant="masonry" cols={3} gap={8}>
                {galleryImages.electrical.map((image, index) => (
                  <ImageListItem key={index}>
                    <img
                      src={image}
                      alt={`Electrical Work ${index + 1}`}
                      loading="lazy"
                      onClick={() => handleImageClick(image, index)}
                      style={{
                        borderRadius: 8,
                        cursor: 'pointer',
                        transition: 'transform 0.3s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.05)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                    />
                  </ImageListItem>
                ))}
              </ImageList>
            )}
          </Box>
        </Container>
      </Box>

      {/* Location & Map Section */}
      <Box id="contact" py={8} sx={{ backgroundColor: 'background.default' }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={6}>
            <Typography variant="h3" component="h2" gutterBottom fontWeight="bold">
              Find Us
            </Typography>
            <Typography variant="h6" color="text.secondary" paragraph>
              Visit our office or get directions to our location
            </Typography>
          </Box>
          
          <Grid container spacing={4} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <Card sx={{ 
                  p: 4, 
                  height: '100%',
                  backgroundColor: 'background.paper',
                  color: 'text.primary',
                }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={3}>
                      <LocationOn sx={{ mr: 2, fontSize: 32, color: 'text.primary' }} />
                      <Typography variant="h5" fontWeight="bold" sx={{ color: 'text.primary' }}>
                        Our Office
                      </Typography>
                    </Box>
                    
                    <Typography variant="body1" paragraph color="text.secondary">
                      36/A, 3rd Cross 24th Main<br />
                      Near Ranga Residency<br />
                      HSR Layout 2nd Sector, Bengaluru 102
                    </Typography>
                    
                    <Box mt={4}>
                      <Typography variant="h6" gutterBottom fontWeight="bold">
                        Business Hours
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Monday - Friday: 9:00 AM - 6:00 PM<br />
                        Saturday: 9:00 AM - 4:00 PM<br />
                        Sunday: Closed
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }} sx={{ display: { xs: 'none', md: 'block' } }}>
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <Map height={500} />
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        py={12}
        sx={{
          backgroundColor: 'background.paper',
          borderTop: 1,
          borderBottom: 1,
          borderColor: 'divider',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Decorative Elements */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '1px',
            background: `linear-gradient(90deg, transparent 0%, ${customTheme.primaryColor} 20%, ${customTheme.accentColor} 80%, transparent 100%)`,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '1px',
            background: `linear-gradient(90deg, transparent 0%, ${customTheme.primaryColor} 20%, ${customTheme.accentColor} 80%, transparent 100%)`,
          }}
        />
        
        {/* Floating Decorative Elements */}
        <Box
          sx={{
            position: 'absolute',
            top: '20%',
            left: '10%',
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${customTheme.primaryColor}20 0%, ${customTheme.accentColor}20 100%)`,
            animation: 'float 6s ease-in-out infinite',
            '@keyframes float': {
              '0%, 100%': { transform: 'translateY(0px) rotate(0deg)' },
              '50%': { transform: 'translateY(-20px) rotate(180deg)' },
            },
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: '60%',
            right: '15%',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${customTheme.accentColor}30 0%, ${customTheme.primaryColor}30 100%)`,
            animation: 'float 8s ease-in-out infinite reverse',
            '@keyframes float': {
              '0%, 100%': { transform: 'translateY(0px) rotate(0deg)' },
              '50%': { transform: 'translateY(-15px) rotate(-180deg)' },
            },
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '30%',
            left: '20%',
            width: '30px',
            height: '30px',
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${customTheme.primaryColor}25 0%, ${customTheme.accentColor}25 100%)`,
            animation: 'float 10s ease-in-out infinite',
            '@keyframes float': {
              '0%, 100%': { transform: 'translateY(0px) rotate(0deg)' },
              '50%': { transform: 'translateY(-10px) rotate(90deg)' },
            },
          }}
        />
        
        <Container maxWidth="lg">
          <Box textAlign="center" position="relative">
            {/* Background Pattern */}
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '200px',
                height: '200px',
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${customTheme.primaryColor}10 0%, ${customTheme.accentColor}10 100%)`,
                zIndex: 0,
              }}
            />
            
            <Box position="relative" zIndex={1}>
              <Typography 
                variant="h2" 
                component="h2" 
                gutterBottom 
                fontWeight="bold"
                sx={{
                  background: `linear-gradient(135deg, ${customTheme.primaryColor} 0%, ${customTheme.accentColor} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 3,
                }}
              >
                Ready to Start Your Project?
              </Typography>
              
              <Typography 
                variant="h5" 
                paragraph 
                sx={{ 
                  color: 'text.secondary',
                  maxWidth: '600px',
                  mx: 'auto',
                  mb: 6,
                }}
              >
                Get a free consultation and quote for your construction project
              </Typography>
              
              <Box display="flex" gap={3} justifyContent="center" flexWrap="wrap">
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<Phone />}
                  onClick={() => handleCallNow('8074635096')}
                  sx={{
                    background: `linear-gradient(135deg, ${customTheme.primaryColor} 0%, ${customTheme.accentColor} 100%)`,
                    minWidth: '200px',
                    height: '48px',
                    '&:hover': {
                      background: `linear-gradient(135deg, ${customTheme.primaryColor}dd 0%, ${customTheme.accentColor}dd 100%)`,
                    },
                  }}
                >
                  Call Now: 8074635096
                </Button>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<Phone />}
                  onClick={() => handleCallNow('9972555661')}
                  sx={{
                    background: `linear-gradient(135deg, ${customTheme.primaryColor} 0%, ${customTheme.accentColor} 100%)`,
                    minWidth: '200px',
                    height: '48px',
                    '&:hover': {
                      background: `linear-gradient(135deg, ${customTheme.primaryColor}dd 0%, ${customTheme.accentColor}dd 100%)`,
                    },
                  }}
                >
                  Call Now: 9972555661
                </Button>
                
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<WhatsApp />}
                  onClick={() => handleWhatsApp('8074635096')}
                  sx={{
                    borderColor: customTheme.accentColor,
                    color: customTheme.accentColor,
                    minWidth: '200px',
                    height: '48px',
                    '&:hover': {
                      borderColor: customTheme.accentColor,
                      backgroundColor: customTheme.accentColor + '10',
                    },
                  }}
                >
                  WhatsApp: 8074635096
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<WhatsApp />}
                  onClick={() => handleWhatsApp('9972555661')}
                  sx={{
                    borderColor: customTheme.accentColor,
                    color: customTheme.accentColor,
                    minWidth: '200px',
                    height: '48px',
                    '&:hover': {
                      borderColor: customTheme.accentColor,
                      backgroundColor: customTheme.accentColor + '10',
                    },
                  }}
                >
                  WhatsApp: 9972555661
                </Button>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Floating Action Buttons */}
      <Fab
        color="primary"
        aria-label="call 8074635096"
        onClick={() => handleCallNow('8074635096')}
        sx={{
          position: 'fixed',
          bottom: 100,
          right: 20,
          background: `linear-gradient(135deg, ${customTheme.primaryColor} 0%, ${customTheme.accentColor} 100%)`,
        }}
      >
        <Phone />
      </Fab>
      
      <Fab
        color="secondary"
        aria-label="whatsapp options"
        onClick={handleWhatsAppOptions}
        sx={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          background: `linear-gradient(135deg, ${customTheme.primaryColor} 0%, ${customTheme.accentColor} 100%)`,
          color: 'white',
          '&:hover': {
            background: `linear-gradient(135deg, ${customTheme.primaryColor}dd 0%, ${customTheme.accentColor}dd 100%)`,
          },
        }}
      >
        <WhatsApp />
      </Fab>

      {/* Service Form Dialog */}
      <Dialog
        open={showForm}
        onClose={handleCloseForm}
        maxWidth="md"
        fullWidth
        disableScrollLock={false}
        onTransitionExited={() => {
          // Ensure body scroll is restored
          document.body.style.overflow = '';
        }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: '90vh',
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <Box position="relative">
            <IconButton
              onClick={handleCloseForm}
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                zIndex: 1,
                backgroundColor: 'rgba(255,255,255,0.9)',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,1)',
                },
              }}
            >
              <Close />
            </IconButton>
            <ServiceForm
              selectedService={selectedService || ''}
              onClose={handleCloseForm}
            />
          </Box>
        </DialogContent>
      </Dialog>

      {/* WhatsApp Options Dialog */}
      <Dialog
        open={showWhatsAppOptions}
        onClose={handleCloseWhatsAppOptions}
        maxWidth="sm"
        fullWidth
        disableScrollLock={false}
        onTransitionExited={() => {
          // Ensure body scroll is restored
          document.body.style.overflow = '';
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <WhatsApp sx={{ color: customTheme.primaryColor }} />
            <Typography variant="h6">Choose WhatsApp Number</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Select which number you'd like to contact on WhatsApp:
          </Typography>
          <Box display="flex" flexDirection="column" gap={2} mt={2}>
            <Button
              variant="outlined"
              startIcon={<WhatsApp sx={{ color: customTheme.accentColor }} />}
              onClick={() => {
                handleCloseWhatsAppOptions();
                // Small delay to ensure dialog closes before navigation
                setTimeout(() => {
                  handleWhatsApp('8074635096');
                }, 100);
              }}
              sx={{
                borderColor: customTheme.accentColor,
                color: customTheme.accentColor,
                minWidth: '200px',
                height: '48px',
                '&:hover': {
                  borderColor: customTheme.accentColor,
                  backgroundColor: customTheme.accentColor + '10',
                },
              }}
            >
              WhatsApp: 8074635096
            </Button>
            <Button
              variant="outlined"
              startIcon={<WhatsApp sx={{ color: customTheme.accentColor }} />}
              onClick={() => {
                handleCloseWhatsAppOptions();
                // Small delay to ensure dialog closes before navigation
                setTimeout(() => {
                  handleWhatsApp('9972555661');
                }, 100);
              }}
              sx={{
                borderColor: customTheme.accentColor,
                color: customTheme.accentColor,
                minWidth: '200px',
                height: '48px',
                '&:hover': {
                  borderColor: customTheme.accentColor,
                  backgroundColor: customTheme.accentColor + '10',
                },
              }}
            >
              WhatsApp: 9972555661
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseWhatsAppOptions} color="primary">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Popup Dialog */}
      <Dialog
        open={!!selectedImage}
        onClose={handleCloseImage}
        maxWidth="lg"
        fullWidth
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.92)',
            backdropFilter: 'blur(8px)',
          },
          '& .MuiDialog-paper': {
            backgroundColor: 'transparent',
            boxShadow: 'none',
            overflow: 'visible',
          },
        }}
      >
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '80vh',
          }}
        >
          {/* Close Button */}
          <IconButton
            onClick={handleCloseImage}
            sx={{
              position: 'absolute',
              top: -50,
              right: 0,
              backgroundColor: 'white',
              color: 'text.primary',
              width: 48,
              height: 48,
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
              '&:hover': {
                backgroundColor: 'grey.100',
              },
              zIndex: 1,
            }}
          >
            <Close />
          </IconButton>

          {/* Previous Button */}
          <IconButton
            onClick={handlePrevImage}
            sx={{
              position: 'absolute',
              left: { xs: 10, md: -60 },
              backgroundColor: 'white',
              color: 'text.primary',
              width: 56,
              height: 56,
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
              '&:hover': {
                backgroundColor: 'grey.100',
                transform: 'scale(1.1)',
              },
              transition: 'all 0.3s ease',
              zIndex: 1,
            }}
          >
            <ArrowBackIos sx={{ ml: 1 }} />
          </IconButton>

          {/* Image Container */}
          <Box
            component="img"
            src={selectedImage || ''}
            alt="Full size view"
            sx={{
              maxWidth: '90%',
              maxHeight: '85vh',
              objectFit: 'contain',
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
            }}
          />

          {/* Next Button */}
          <IconButton
            onClick={handleNextImage}
            sx={{
              position: 'absolute',
              right: { xs: 10, md: -60 },
              backgroundColor: 'white',
              color: 'text.primary',
              width: 56,
              height: 56,
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
              '&:hover': {
                backgroundColor: 'grey.100',
                transform: 'scale(1.1)',
              },
              transition: 'all 0.3s ease',
              zIndex: 1,
            }}
          >
            <ArrowForwardIos />
          </IconButton>

          {/* Image Counter */}
          <Box
            sx={{
              position: 'absolute',
              bottom: -50,
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: 'white',
              px: 3,
              py: 1.5,
              borderRadius: 3,
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            }}
          >
            <Typography variant="body2" fontWeight="600">
              {currentImageIndex + 1} / {getCurrentGallery().length}
            </Typography>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};

export default Home;

